import type { DeviceConditionUI } from '../../../../types/DeviceCondAndTriggerUI';
import { ID_TO_I18N_CONDITIONS_EQUAL, ID_TO_I18N_PROP } from './conditions_to_i18n';
import { celsius_to_fahrenheit, isProThreeEM, isThreeEM } from 'src/helpers/interface_helpers';
import { equal_to_label_list, less_than_more_than_to_label_list } from './conditions_to_label_list';

import { _init_time_based_conditions } from '../../helpers/init_time_conditions';
import { capFirstChar, indexToAlphabet } from 'src/helpers/utilities';
import { conditionTypeNameMap, diffuserConditions } from '../../helpers/init_device_conditions';
import { condition_to_character } from '../../../../helpers/handle_manage_old_scenes';
import { exchangeMarketCountryAndRegionNames } from '../../helpers/utilities_ui_to_scenes';
import { getPresenceZoneName } from '../../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';

import { TEMPERATURE_SENSORS_IN_CELSIUS, week_days } from '../../../../constants';
import {
	get_vc_condition_label,
	get_vc_condition_list_item,
} from '../../helpers/virtual_components/virtual_device_preview';
import { isSupportedXT1Peripheral, isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { getMathParamConditionListItem } from '../../helpers/math_based/math_param_utils';
import { isInvalidWeatherCondition } from '../../helpers/has_missing_scene_data';

let _format;
format.subscribe((e) => (_format = e));

/** --------- DEVICE BASED ---------**/

export function _device_condition_msg(translated: DeviceConditionUI, device_id, gen) {
	let subtitle = '',
		conditionList = [],
		labelList = [];
	// get units

	const device = get(devices)[device_id];
	//@ToDo: Extract this to a helper function as parf of VC in Scenes
	if (device?.category === 'virtual_device') {
		conditionList.push(get_vc_condition_list_item(translated));
	}

	const units = condition_to_character(
		translated.cond_name,
		!TEMPERATURE_SENSORS_IN_CELSIUS.includes(device?.type)
			? devices_settings.getByRealId(device_id)
			: device,
	);

	// Special handling for presence sensor (S4SN-0U61X) conditions
	if (device?.type === 'S4SN-0U61X') {
		// Add condition type info
		if (translated.cond_type !== undefined) {
			const { cond_type } = translated;
			const { cond_type_name, valid_for_min } = cond_type;

			conditionList.push({
				name: `${capFirstChar(_format('scene_label_trigger'))}; ${capFirstChar(
					_format(conditionTypeNameMap[cond_type_name]),
				)}`,
				icon: 'fa-solid fa-circle-play',
				type: 'trigger',
			});

			if (Number(valid_for_min) > 0) {
				conditionList.push({
					name: `${_format('condition_type_condition_duration', {
						values: { duration: valid_for_min },
					})}`,
					icon: 'fa-solid fa-stopwatch',
					type: 'duration',
				});
			}
		} else {
			conditionList.push({
				name: `${capFirstChar(_format('scene_label_condition'))}`,
				icon: 'fa-solid fa-list-check',
				type: 'condition',
			});
		}

		// Handle zone-based presence conditions
		if (translated.cond_name === 'presence_zone_state' && translated.add_info_ind) {
			// Zone state condition (presence/absence)
			const zoneId = translated.add_info_ind;
			const isPresence = translated.value === 'true';
			const zoneName = getPresenceZoneName(
				String(zoneId),
				translated.device?.id,
				get(devices_settings),
			);
			subtitle = `${zoneName} ${isPresence ? 'presence' : 'absence'} detected`;

			labelList.push({
				name: zoneName,
				icon: 'fa-solid fa-location-dot',
				type: 'zone',
			});

			labelList.push({
				name: isPresence ? 'Presence' : 'Absence',
				icon: isPresence ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash',
				type: 'state',
			});
		} else if (translated.cond_name === 'presence_zone_objects' && translated.add_info_ind) {
			// Zone object count condition
			const zoneId = translated.add_info_ind;
			const operator = translated.operator;
			const value = translated.value;
			const zoneName = getPresenceZoneName(
				String(zoneId),
				translated.device?.id,
				get(devices_settings),
			);

			const objectWord = Number(value) === 1 ? 'object' : 'objects';
			let label = '';
			switch (operator) {
				case 'gt':
					label = `Has more than ${value} ${objectWord}`;
					break;
				case 'lt':
					label = `Has less than ${value} ${objectWord}`;
					break;
				case 'eq':
				default:
					label = `Has ${value} ${objectWord}`;
			}

			subtitle = `${zoneName} ${label.toLowerCase()}`;

			labelList.push({
				name: zoneName,
				icon: 'fa-solid fa-location-dot',
				type: 'zone',
			});

			labelList.push({
				name: label,
				icon: 'fa-solid fa-users',
				type: 'count',
			});
		} else if (translated.cond_name === 'presence_zone_objects_delta' && translated.add_info_ind) {
			// Zone enter/leave delta condition
			const zoneId = translated.add_info_ind;
			const operator = translated.operator;
			const zoneName = getPresenceZoneName(
				String(zoneId),
				translated.device?.id,
				get(devices_settings),
			);

			// more_than = enter, less_than = leave, equal = legacy fallback → enter
			const isEnter = operator === 'more_than' || operator === 'equal';
			const actionLabel = isEnter
				? _format('option_presencezone_enter', { default: 'When enter zone' })
				: _format('option_presencezone_leave', { default: 'When leave zone' });
			subtitle = `${zoneName}: ${actionLabel}`;

			labelList.push({
				name: zoneName,
				icon: 'fa-solid fa-location-dot',
				type: 'zone',
			});

			labelList.push({
				name: actionLabel,
				icon: isEnter ? 'fa-solid fa-person-walking' : 'fa-solid fa-person',
				type: 'state',
			});
		} else if (translated.cond_name === 'luxury') {
			// Discrete band luminosity — delegate to the same helper SHMOS-01 uses
			const item = equal_to_label_list('luxury', translated.value, translated);
			subtitle = String(item?.value ?? translated.value);
			labelList.push(item);
		} else if (translated.cond_name === 'lux_i' || translated.cond_name === 'illumination') {
			// Numeric lux (`lux_i` in scene service; UI `cond_name` is `illumination`)
			const label = less_than_more_than_to_label_list(
				'illumination',
				translated.value,
				translated.operator,
				undefined,
			);
			subtitle = String(label?.value ?? `${translated.value} lux`);
			labelList.push(label);
		}

		// Return early for presence sensors to avoid generic processing
		return { subtitle, conditionList, labelList };
	}

	if (
		/^(light|relay|inputs_reader)$/.test(device?.category) &&
		(translated.cond_name === 'temperature' || translated.cond_name === 'humidity')
	) {
		const name = _format(`label_add_on`, {
			default: `add-on`,
		});
		conditionList.push({
			name: capFirstChar(name),
			icon: 'fa-solid fa-circle-info',
			type: 'phase',
		});
	}

	// logic for 3phases e-meter. Handles gen.1 3EM and PRO3EM in triphase mode
	if (isThreeEM(device?.type)) {
		let name = '';

		if (isProThreeEM(device?.type) && device.mode === 'monophase') {
			if (translated.cond_name == 'turn_add_on') {
				name = _format(`label_add_on`, {
					default: `add-on`,
				});
				conditionList.push({
					name: capFirstChar(name),
					icon: 'fa-solid fa-circle-info',
					type: 'phase',
				});
			}
		} else {
			if (translated.cond_name == 'total_current') {
				name = _format(`label_sum_of_phases`, {
					default: `sum of all phases`,
				});
			} else if (translated.cond_name == 'turn_add_on') {
				name = _format(`label_add_on`, {
					default: `add-on`,
				});
			} else {
				const phaseLetter = indexToAlphabet(Number(translated.device.channel));
				name = _format(`label_phase_${phaseLetter.toLowerCase()}`, {
					default: `phase ${phaseLetter}`,
				});
			}

			if (!isProThreeEM(device?.type) && translated.cond_name === 'turn') name = '';

			if (name !== '') {
				conditionList.push({
					name: capFirstChar(name),
					icon: 'fa-solid fa-circle-info',
					type: 'phase',
				});
			}
		}
	}

	if (translated.cond_type !== undefined) {
		const { cond_type } = translated;
		const { cond_type_name, valid_for_min } = cond_type;

		conditionList.push({
			name: `${capFirstChar(_format('scene_label_trigger'))}; ${capFirstChar(
				_format(conditionTypeNameMap[cond_type_name]),
			)}`,
			icon: 'fa-solid fa-circle-play',
			type: 'trigger',
		});

		if (Number(valid_for_min) > 0) {
			conditionList.push({
				name: `${_format('condition_type_condition_duration', {
					values: { duration: valid_for_min },
				})}`,
				icon: 'fa-solid fa-stopwatch',
				type: 'duration',
			});
		}
	} else {
		conditionList.push({
			name: `${capFirstChar(_format('scene_label_condition'))}`,
			icon: 'fa-solid fa-list-check',
			type: 'condition',
		});
	}

	// extract external sensor/addon name
	if (translated?.sensor_name) {
		subtitle =
			translated.cond_name === 'digital_input_add_on' ||
			translated.cond_name === 'push_type_add_on'
				? `(${translated.sensor_name}) ${ID_TO_I18N_CONDITIONS_EQUAL(
						translated.cond_name,
						(translated.value =
							units.includes('F') && translated.cond_name === 'temperature'
								? '' + celsius_to_fahrenheit(Number(translated.value))
								: translated.value),
						gen,
						translated,
					)}`
				: _format('when_external_sensor_msg', {
						values: {
							sensor_name: translated.sensor_name,
							value: translated.value,
							operator: _format(`operator_${translated.operator.replace(' ', '_')}`),

							character: condition_to_character(
								translated.cond_name,
								!TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type)
									? undefined
									: device,
							),
							prop_name: _format(`prop_${translated.cond_name}`),
						},
						default: `When ${translated.cond_name} is ${translated.value}`,
					});

		labelList.push(
			['digital_input_add_on', 'push_type_add_on'].includes(translated.cond_name)
				? equal_to_label_list(translated.cond_name, translated.value, translated)
				: less_than_more_than_to_label_list(
						translated.cond_name,
						(translated.value =
							units.includes('F') &&
							(translated.cond_name === 'temperature' ||
								translated.cond_name === 'trv_target_temperature')
								? '' + celsius_to_fahrenheit(Number(translated.value))
								: translated.value),
						translated.operator,
						units,
					),
		);

		return {
			subtitle,
			labelList,
			conditionList,
		};
	}
	if (
		device?.category === 'virtual_device' ||
		// The device is an XT1 module, and the condition does not include an addon peripheral sensor
		(isXT1Module(device?.type) && !isSupportedXT1Peripheral(translated.cond_name))
	) {
		labelList.push(get_vc_condition_label(translated));
	} else if (translated.operator === 'equal') {
		labelList.push(equal_to_label_list(translated.cond_name, translated.value, translated));

		// conditionList.push(equal_to_condition_list(translated.cond_name, translated.cond_type, translated.value));
		subtitle = ID_TO_I18N_CONDITIONS_EQUAL(translated.cond_name, translated.value, gen, translated);
	} else {
		const label = less_than_more_than_to_label_list(
			translated.cond_name,
			(translated.value =
				units.includes('F') &&
				(translated.cond_name === 'temperature' ||
					translated.cond_name === 'trv_target_temperature')
					? '' + celsius_to_fahrenheit(Number(translated.value))
					: translated.value),
			translated.operator,
			translated.cond_name === 'temperature' || translated.cond_name === 'trv_target_temperature'
				? units
				: undefined,
			translated.cond_name === 'scroll_steps' ? translated.remote_control_channel : undefined,
		);

		// Custom labels for Cury conditions
		if (diffuserConditions.includes(translated.cond_name)) {
			//E.g. Intensity L is more than 50%
			label.value = `${_format(`scene_label_${translated.cond_name}`, {
				default: translated.cond_name.replace('_left', ' L').replace('_right', ' R') + ' is',
			})} ${label.value}`;
		}

		labelList.push(label);

		// Custom subtitle for Cury diffuser conditions
		if (diffuserConditions.includes(translated.cond_name)) {
			const isLeft = translated.cond_name.includes('_left');
			const slotText = isLeft
				? _format('left_slot', { default: 'Left' }).toLowerCase()
				: _format('right_slot', { default: 'Right' }).toLowerCase();
			const propertyName = translated.cond_name.replace('_left', '').replace('_right', '');

			subtitle = `${capFirstChar(slotText)} slot: ${propertyName} ${_format(`operator_${translated.operator.replace(' ', '_')}`)} ${translated.value}${condition_to_character(translated.cond_name)}`;
		} else {
			const operatorLabel = _format(`operator_${translated.operator.replace(' ', '_')}`);
			subtitle = _format('scene_condition_input_value', {
				values: {
					key: ID_TO_I18N_PROP(translated.cond_name, gen),
					operator: operatorLabel,
					value: translated.value,
					units: condition_to_character(
						translated.cond_name,
						!TEMPERATURE_SENSORS_IN_CELSIUS.includes(device?.type) ? undefined : device,
					),
				},
			});
		}
	}

	return {
		subtitle,
		labelList,
		conditionList,
	};
}

/** -------- END DEVICE BASED ---------*/

/** ------- TIME BASED CONDITIONS ------*/
export function findIcon(translated) {
	let icon = _init_time_based_conditions[translated?.id]?.props?.icon || undefined;

	return { icon };
}

export function _time_based_condition_msg(translated) {
	let subtitle, description;
	if (['number', 'string'].includes(typeof translated.value)) {
		//timer
		subtitle =
			_format('interval_value_and_units', {
				values: {
					value_and_units: _format(`delay_units_minutes`, {
						values: { n: translated.value },
					}),
				},
			}) || `Interval: ${translated.value} min.`;
	} else if (translated.value.isin) {
		subtitle = translated.value.isin.map((el) => {
			if (week_days[el]?.i18n) {
				return _format(week_days[el]?.i18n);
			}
			return el;
		});
		description = `${_format('execution_time')}: ${translated.value.time} `;
	} else {
		//once (selected date)
		subtitle = `${_format('execute_at')}: ${translated.value.date}`;
		description = `${_format('execution_time')}: ${translated.value.time} `;
	}

	return {
		subtitle,
		description,
	};
}

/** --------- END TIME BASED CONDITIONS -----*/

/** ----------- SUNRISE/SUNSET CONDITIONS------ */
export function _sunrise_sunset_condition_message(translated) {
	let subtitle = '',
		description = '',
		labelList = [];

	let { hours, mins, beforeAfter, type } = translated;

	if (hours === 0) {
		subtitle = _format(`${type}_condition_msg_only_mins`, {
			values: {
				mins,
				beforeAfter: _format(`text_${beforeAfter}`, {
					default: beforeAfter === 'before' ? 'before' : 'after',
				}),
			},
			default: `${mins} minutes ${beforeAfter} ${type === 'sunrise ' ? 'sunrise' : 'sunset'}`,
		});
	} else {
		subtitle = _format(`${type}_condition_msg_hours_mins`, {
			values: {
				mins,
				hours,
				beforeAfter: _format(`text_${beforeAfter}`, {
					default: beforeAfter === 'before' ? 'before' : 'after',
				}),
			},
			default: `${hours} hours ${mins} minuntes ${beforeAfter} ${type === 'sunrise ' ? 'sunrise' : 'sunset'}`,
		});
	}

	let time = `${hours < 10 ? '0' + hours : hours}:${mins < 10 ? '0' + mins : mins}`;
	labelList.push(
		{
			icon: `fas fa-${type}`,
			value:
				capFirstChar(
					_format(`text_${beforeAfter}`, {
						default: beforeAfter === 'before' ? 'before' : 'after',
					}),
				) +
				' ' +
				_format(`schedule_opt_${type}`),
		},
		{
			icon: 'fas fa-clock',
			value: beforeAfter === 'after' ? `+${time}` : `-${time}`,
		},
	);

	return {
		subtitle,
		description,
		labelList,
	};
}
/** ----------- END SUNRISE/SUNSET CONDITIONS------ */

/** --------- WEATHER CONDITIONS ------ */

export const _weather_condition_message = (translated) => {
	let subtitle = '',
		description = '',
		labelList = [],
		conditionList = [];

	let { cond, op: operator, value, hours = 1 } = translated;
	let time = `0${hours}:00`;

	if (translated.execution_type) {
		conditionList.push({
			name: `${capFirstChar(_format('scene_label_trigger'))}; ${capFirstChar(_format(conditionTypeNameMap[translated.execution_type]))}`,
			icon: 'fa-solid fa-play',
			type: 'trigger',
		});
	}

	switch (cond) {
		case 'clouds':
			const isInvalidData = isInvalidWeatherCondition({
				condition: cond,
				value: translated.thsval,
			});
			subtitle = _format(`cond_weather_when_${value.replace(/\s/g, '_')}`, {
				default: 'When ' + value,
			});

			labelList.push({
				name: 'clouds',
				icon: 'fad fa-clouds',
				value: isInvalidData
					? _format('weather_forecast_invalid_data', {
							default: 'Invalid weather forecast condition',
						})
					: _format('label_' + value.replace(/\s/g, '_')),
			});
			break;
		case 'wind_direction':
			subtitle = _format(`cond_weather_when_wind_dir_${value.replace(/-/g, '_')}`);
			labelList.push({
				name: cond,
				icon: 'fal fa-wind',
				value: _format('label_' + value.replace(/-/g, '_')),
			});
			break;
		case 'wind_force':
			subtitle = value;
			labelList.push({
				name: 'wind_force',
				icon: 'fal fa-wind',
				value: _format('option_' + value.replace(/\s/g, '_')).toLowerCase(),
			});
			break;
		case 'chance_precipitation':
			subtitle = value;
			labelList.push({
				name: 'precipitation',
				icon: 'fal fa-raindrops',
				value:
					_format('opt_chance_precipitation').toLowerCase() +
					':  ' +
					_format(`operator_${operator.replace(' ', '_')}`) +
					' ' +
					value +
					'%',
			});
			break;
		case 'wind_speed':
			subtitle = String(value);
			labelList.push({
				name: cond,
				icon: 'fal fa-wind',
				value: _format(`operator_${operator.replace(/\s/g, '_')}`) + ' ' + value + ' km/h',
			});
			break;
		// case 'rain':
		// 	subtitle = _format(`cond_weather_when_${ value.replace(/\s/g, '_') }`);
		// 	labelList.push({
		// 		name: cond,
		// 		icon: 'fad fa-cloud-rain',
		// 		value: _format('label_' + value.replace(/\s/g, '_'))
		// 	});
		// 	break;
		// case 'snow':
		// 	subtitle = _format(`cond_weather_when_${ value.replace(/\s/g, '_') }`);
		// 	labelList.push({
		// 		name: cond,
		// 		icon: 'fal fa-snowflake',
		// 		value: _format('label_' + value.replace(/\s/g, '_'))
		// 	});
		// 	break;
		case 'temperature':
			subtitle = _format(`cond_weather_when_temperature`, {
				values: {
					operator: _format(`operator_${operator.replace(/\s/g, '_')}`),
					temp: value,
					units: 'C',
				},
			});
			labelList.push({
				name: cond,
				icon: 'fal fa-temperature-list',
				value: _format(`operator_${operator.replace(' ', '_')}`) + ' ' + value + '°C',
			});
			break;
		case 'humidity':
			subtitle = _format(`cond_weather_humidity`, {
				values: {
					operator: _format(`operator_${operator.replace(/\s/g, '_')}`),
					humidity: value,
				},
			});
			labelList.push({
				name: cond,
				icon: 'fal fa-droplet-percent',
				value: _format(`operator_${operator.replace(' ', '_')}`) + ' ' + value + '%',
			});
			break;
		default:
			subtitle = cond + ' ' + value;
	}
	if (labelList.length > 0) {
		labelList.push({
			icon: 'fas fa-clock',
			value: `+ ${time}`,
		});
	}
	return {
		subtitle,
		description,
		labelList,
		conditionList,
	};
};

/** ----------- END WEATHER CONDITION -------- */

/** ----------- MATH CONDITION------ */
export function _math_condition_message(translated) {
	let subtitle = '',
		description = '',
		labelList = [],
		conditionList = [];

	// Properties of the Math Condition
	let { expression, params } = translated;

	subtitle = _format('title_math_expression', {
		default: 'Math expression',
	});

	// Add the expression to the labelList, displayed under the title
	labelList.push({
		icon: ``,
		value: expression,
	});

	// Add the parameters to the conditionList
	conditionList = params?.map((param) => getMathParamConditionListItem(param));

	return {
		subtitle,
		description,
		labelList,
		conditionList,
	};
}

/** ----------- END MATH CONDITION -------- */

/** ------------ Energy exchange ---------- */

export const _energy_exchange_message = (translated) => {
	let { selectedCountry = { title: '' }, selectedRegion = { title: '' } } =
		exchangeMarketCountryAndRegionNames(translated.exchange_market);
	let countryName = _format(selectedCountry.title);
	let regionName = _format(selectedRegion.title);
	let subtitle = '',
		description = '',
		labelList = [];

	if (translated.cond_name === 'energy_exchange') {
		subtitle = `${countryName} ${regionName}`;
		labelList.push({
			name: translated.cond_name,
			icon: 'fa fa-money-bill-trend-up',
			value:
				_format(`operator_${translated.operator.replace(' ', '_')}`) +
				' ' +
				translated.value +
				' ' +
				translated.character,
		});
	}

	return {
		subtitle,
		description,
		labelList,
	};
};
/** ------------ END Energy exchange ---------- */
